import { useState, useEffect, useCallback } from 'react';

import { getHVACFilterStatus } from '../graphql/queries';
import { HVACFilterStatus } from '../types';
import { API } from '../utils/amplifyBootstrap';

export interface HVACFilterStatusResult {
  data: HVACFilterStatus | null;
  loading: boolean;
  error: Error | null;
  refresh: () => void;
}

export const useHVACFilterStatus = (sensorId: string | undefined): HVACFilterStatusResult => {
  const [data, setData] = useState<HVACFilterStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchFilterStatus = useCallback(async () => {
    if (!sensorId) {
      setData(null);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response: any = await API.graphql({
        query: getHVACFilterStatus,
        variables: { sensorId },
      });

      if (response?.data?.getHVACFilterStatus) {
        setData(response.data.getHVACFilterStatus);
      } else {
        setData(null);
        setError(new Error('HVAC filter status not available for this sensor'));
      }
    } catch (err) {
      console.error('Error fetching HVAC filter status:', err);
      setError(err instanceof Error ? err : new Error('Unknown error'));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [sensorId]);

  useEffect(() => {
    fetchFilterStatus();
  }, []);

  const refresh = () => {
    fetchFilterStatus();
  };

  return {
    data,
    loading,
    error,
    refresh,
  };
};
